/**
 * Renders a list of content segments produced by the Python
 * ``serialize_content_blocks`` function.
 *
 * SSR-capable blocks (handled in ``renderSSRContent``) are rendered as
 * proper React children.  Everything else is rendered as raw HTML via
 * ``dangerouslySetInnerHTML``; only ``raw_html`` segments are marked as
 * trusted editor HTML.
 *
 * This avoids the nested-React-root problem where an ``include_react``
 * block inside a conditional block's ``dangerouslySetInnerHTML`` would lose
 * event binding and hydration.
 */
import { Immutable } from "immer";
import React, { createElement, useMemo } from "react";

import { CompatibilityAccordionModule, ValuePropBarBlock } from "@reactivated";

import { EditorContent } from "@thelabnyc/thelabui/src/components/EditorContent";

import { CompatibilityAccordion } from "../../../components/CompatibilityAccordion";
import { ValuePropsBarBlock } from "../../../components/ValuePropsBarBlock";

// ---------------------------------------------------------------------------
// Segment types – must stay in sync with the Python ``ContentSegment`` shape
// ---------------------------------------------------------------------------

interface IHtmlSegment {
    readonly type: "html";
    readonly html: string;
}

interface IRawHtmlSegment {
    readonly type: "raw_html";
    readonly html: string;
}

interface IWrapperInfo {
    readonly tag: string;
    readonly className: string;
}

interface ICompatibilityAccordionSegment {
    readonly type: "compatibility_accordion";
    readonly props: { readonly cms_json: string };
    readonly wrapper?: IWrapperInfo;
}

interface IValuePropBarSegment {
    readonly type: "value_prop_bar";
    readonly props: { readonly cms: string };
    readonly wrapper?: IWrapperInfo;
}

type ContentSegment =
    | IHtmlSegment
    | IRawHtmlSegment
    | ICompatibilityAccordionSegment
    | IValuePropBarSegment;

type SSRSegment = Exclude<ContentSegment, IHtmlSegment | IRawHtmlSegment>;

// ---------------------------------------------------------------------------
// Rendering helpers
// ---------------------------------------------------------------------------

const ALLOWED_WRAPPER_TAGS = new Set([
    "section",
    "div",
    "article",
    "aside",
    "span",
    "main",
    "header",
    "footer",
]);

const renderSSRContent = (segment: SSRSegment): React.ReactNode => {
    switch (segment.type) {
        case "compatibility_accordion": {
            // The configurator segment serializer still emits cms_json (a JSON
            // string); parse it at this boundary into the typed value the
            // reactivated-ported component now expects (mirrors value_prop_bar).
            if (!segment.props.cms_json) {
                return null;
            }
            const parsed: CompatibilityAccordionModule = JSON.parse(
                segment.props.cms_json,
            );
            return <CompatibilityAccordion value={parsed} />;
        }
        case "value_prop_bar": {
            if (!segment.props.cms) {
                return null;
            }
            const parsed: ValuePropBarBlock = JSON.parse(segment.props.cms);
            return <ValuePropsBarBlock valuePropsData={parsed} />;
        }
        default: {
            const _exhaustive: never = segment;
            return _exhaustive;
        }
    }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type IProps = Immutable<{
    contentSegments: string;
}>;

export const ConditionalBlockContent = (props: IProps): React.JSX.Element => {
    const segments: readonly ContentSegment[] = useMemo(
        () => JSON.parse(props.contentSegments) as ContentSegment[],
        [props.contentSegments],
    );

    return (
        <>
            {segments.map((segment, i) => {
                if (segment.type === "raw_html") {
                    return (
                        <EditorContent key={i}>
                            {(marker) => (
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: segment.html,
                                    }}
                                    {...marker}
                                />
                            )}
                        </EditorContent>
                    );
                }
                if (segment.type === "html") {
                    return (
                        <div
                            key={i}
                            dangerouslySetInnerHTML={{ __html: segment.html }}
                        />
                    );
                }

                const content = renderSSRContent(segment);

                if (segment.wrapper) {
                    const tag = ALLOWED_WRAPPER_TAGS.has(segment.wrapper.tag)
                        ? segment.wrapper.tag
                        : "div";
                    return createElement(
                        tag,
                        { key: i, className: segment.wrapper.className },
                        content,
                    );
                }

                return <React.Fragment key={i}>{content}</React.Fragment>;
            })}
        </>
    );
};
